import React, { useState, useEffect } from "react";
import "../styles/FaqPage.css";
import { getFaqPage, getFaqs, getFaqCategories } from "../lib/api";
import { Page, PageHeader, FilterChips } from "../components/ui";

export default function FaqPage() {
  const [page, setPage] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [openIndex, setOpenIndex] = useState(-1);

  useEffect(() => {
    Promise.all([getFaqPage(), getFaqs(), getFaqCategories()])
      .then(([pageRes, faqsRes, catsRes]) => {
        setPage(pageRes);
        setItems(faqsRes);
        setCategories(catsRes);
      })
      .catch((err) => {
        console.error(err);
        setError(err);
      });
  }, []);

  const filteredItems =
    activeCategory === "Alle"
      ? items
      : items.filter((item) => item.category === activeCategory);

  const contactEmail = page?.contactEmail || "Direktion.912081@schule.wien.gv.at";

  return (
    <Page className="faqp-page" loading={!page} error={error}>
      <PageHeader
        parent={{ label: "Eltern & Schule", to: "/mitgestalten" }}
        current={page?.heroTitle || "Wissenswert"}
        title={page?.heroTitle || "Wissenswert"}
        subtitle={page?.heroSubtitle}
      />

      <section className="faqp-section">
        <div className="container">
          <FilterChips
            options={categories}
            value={activeCategory}
            allLabel="Alle"
            label="FAQ Kategorien"
            onChange={(val) => {
              setActiveCategory(val);
              setOpenIndex(-1);
            }}
          />

          <div className="faqp-layout">
            <div className="faqp-list" aria-label="FAQ Liste">
              {filteredItems.map((item, idx) => {
                const isOpen = idx === openIndex;
                return (
                  <article className={`faqp-item ${isOpen ? "is-open" : ""}`} key={item.id}>
                    <button
                      type="button"
                      className="faqp-question"
                      aria-expanded={isOpen ? "true" : "false"}
                      onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                    >
                      <div className="faqp-question__meta">
                        <span className="faqp-pill">{item.category}</span>
                        <h3>{item.question}</h3>
                      </div>
                      <span className="faqp-iconWrap" aria-hidden="true">
                        <IconChevron down={!isOpen} />
                      </span>
                    </button>
                    {isOpen ? (
                      <div className="faqp-answer">
                        <FormattedText text={item.answer} />
                      </div>
                    ) : null}
                  </article>
                );
              })}
              {filteredItems.length === 0 ? (
                <p style={{ color: "rgba(16,17,20,0.5)", padding: "24px 0" }}>
                  Keine Einträge in dieser Kategorie.
                </p>
              ) : null}
            </div>

            <aside className="faqp-side">
              <div className="faqp-sideCard">
                <div className="faqp-badge">
                  <IconInfo />
                  <span>Hinweis</span>
                </div>
                <h2>{page?.sideTitle || "Noch eine Frage offen?"}</h2>
                <p>
                  {page?.sideText ||
                    "Nicht jede Frage lässt sich allgemein beantworten. Wenn Sie etwas Spezifisches wissen möchten, schreiben Sie uns gerne."}
                </p>
                <div className="faqp-sideLinks">
                  <button
                    type="button"
                    className="faqp-btn faqp-btn--primary"
                    onClick={() => {
                      window.location.href = `mailto:${contactEmail}`;
                    }}
                  >
                    Mail schreiben
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </Page>
  );
}

/**
 * Turns the plain text from Strapi into proper paragraphs and lists:
 *  - a blank line          → new paragraph block, with visible spacing
 *  - a normal line break   → <br> inside the paragraph
 *  - a line starting with • - or *  → real list item (<ul><li>),
 *    so the spacing after the bullet and the indent of wrapped
 *    lines are handled by CSS instead of typed spaces (which the
 *    browser collapses into one).
 *
 * Each block gets its own wrapper with the spacing set directly
 * (style), so a blank line in Strapi ALWAYS shows as a gap — even
 * if FaqPage.css is out of date.
 */
const BULLET = /^\s*[•\-–*]\s*/;
const BLOCK_GAP = "1.1em";  // gap for a blank line in Strapi
const INNER_GAP = "0.35em"; // gap between an intro line and its list

function FormattedText({ text }) {
  if (!text) return null;

  const blocks = String(text)
    .replace(/\r\n/g, "\n")
    .split(/\n\s*\n/) // blank line = new block
    .map((block) => block.split("\n").filter((line) => line.trim() !== ""))
    .filter((lines) => lines.length > 0);

  return (
    <>
      {blocks.map((lines, b) => {
        // Group consecutive lines into paragraph runs and list runs
        const groups = [];
        lines.forEach((line) => {
          const isBullet = BULLET.test(line);
          const last = groups[groups.length - 1];
          const type = isBullet ? "list" : "para";
          const content = isBullet ? line.replace(BULLET, "") : line.trim();
          if (last && last.type === type) {
            last.lines.push(content);
          } else {
            groups.push({ type, lines: [content] });
          }
        });

        return (
          <div
            key={b}
            className="faqp-answer__block"
            style={b > 0 ? { marginTop: BLOCK_GAP } : undefined}
          >
            {groups.map((group, g) => {
              const gap = g > 0 ? { marginTop: INNER_GAP } : undefined;
              return group.type === "list" ? (
                <ul key={g} style={gap}>
                  {group.lines.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p key={g} style={gap}>
                  {group.lines.map((line, i) => (
                    <React.Fragment key={i}>
                      {i > 0 ? <br /> : null}
                      {line}
                    </React.Fragment>
                  ))}
                </p>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

function Svg({ children, size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}
function IconChevron({ down = true }) {
  return (
    <Svg>
      <path d={down ? "M6 9l6 6 6-6" : "M6 15l6-6 6 6"} stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}
function IconInfo() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 10v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M12 7.2h.01" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
    </Svg>
  );
}